import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { comics } from '@/mocks/comics';

export default function CategoryDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryComics = comics.filter((c) => c.categorySlug === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-20 flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-500">分类未找到</p>
            <Link to="/category" className="text-primary-500 text-sm mt-2 inline-block">返回分类列表</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name}韩漫 - 韩国漫画网`,
    description: `在线阅读${category.name}类好看的韩国漫画，${category.description}。韩国漫画网为您精选优质${category.name}韩漫。`,
    url: `http://dakunkj.cn/category/${slug}`,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categoryPageSchema)}</script>
      <SEOHead />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-20">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '全部分类', href: '/category' },
                { label: `${category.name}韩漫` },
              ]}
            />

            <div className="mt-8">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center bg-accent-100 rounded-lg">
                  <i className={`${category.icon} text-3xl text-accent-600`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                    {category.name}韩漫
                  </h1>
                  <p className="mt-1 text-sm text-foreground-600">
                    {category.description} · 共 {categoryComics.length} 部作品
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-1.5 inline-flex flex-wrap gap-1">
                {categories.map((cat) => {
                  const isActive = cat.slug === slug;
                  return (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                        isActive
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:text-primary-500 hover:bg-background-50'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="mt-10">
              {categoryComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                  {categoryComics.map((comic) => (
                    <ComicCard key={comic.id} {...comic} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <p className="text-foreground-500">该分类暂无漫画</p>
                  <Link to="/category" className="text-primary-500 text-sm mt-2 inline-block">
                    浏览其他分类
                  </Link>
                </div>
              )}
            </div>

            <div className="mt-16 p-8 bg-accent-50 rounded-lg border border-accent-100">
              <h2 className="font-heading text-lg font-semibold text-foreground-900">
                关于{category.name}韩漫
              </h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                {category.name}类韩国漫画是韩漫中最受欢迎的类型之一。在韩国漫画网，我们精心收录了{category.count}部{category.name}题材的韩漫作品，
                涵盖多种风格和主题。无论您是韩漫老读者还是初次接触，都能在这里找到心仪的漫画。下拉式阅读模式让您享受流畅的浏览体验，
                快来开启您的{category.name}韩漫之旅吧。
              </p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}